import React, {
	useContext, useEffect, useMemo, useState,
} from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';

import { IconChevronRightSmall, Button } from '@sololearnorg/sol';
import classNames from 'classnames';
import { Container, i18n } from '../../../../symphony';

import {
	SlAISolutionsContext,
	SolutionActionType,
	SolutionType,
} from '../../global-constants';
import { SlActions } from '../../services/ActionsService/sl-actions';
import {
	ActionType,
	CloseAISolutionsClickSource,
	IAnswerExplanationData,
	IQuestionData,
	ISolution,
	RequestType,
} from '../../global-interface';
import { SlDataService } from '../../services/sl-data.service';
import { AISolution } from './AISolution';
import { SolotutorIcon } from './solution-icons/SolotutorIcon';
import { AIAnswerExplanation } from '../AIAnswerExplanation/AIAnswerExplanation';
import { KodieContext } from '../../services/context';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { SlExternalWindowPanel } from '../../../../shared/public/SlExternalWindowPanel/SlExternalWindowPanel';

import s from './AISolutions.scss';
import { IMaterialHeader, MaterialType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { ICode } from '../../../../api/public/code-assistant';
import { KodieIcon } from '../../../../shared/public/assets/icons/KodieIcon';

export interface IAISolutionsProps {
	isTIYWidget?: boolean;
	isLightMode?: boolean;
	solutionsData: {
		materialType: MaterialType;
		materialHeader?: IMaterialHeader;
		codes?: ICode[];
		problem?: string;
		session?: string;
		solution?: ISolution;
		lessonContent?: string;
		courseName?: string;
		questionData?: IQuestionData;
		answerExplanationData?: IAnswerExplanationData;
	};
	className?: string;
	OpenButton?: React.ComponentType<{ onClick: () => void }>;
	solutionType?: SolutionType;
}

export function AISolutions({
	solutionsData,
	className,
	OpenButton,
	solutionType = SolutionType.SidePanel,
	isTIYWidget,
	isLightMode,
}: IAISolutionsProps) {
	useStyles(s);
	const { context } = useContext(KodieContext);
	const { actions$ } = Container.take(SlAISolutionsContext, SlActions);
	const [isOpened, setIsOpened] = useState(false);
	const [isWrapperOpened, setIsWrapperOpened] = useState(false);
	const {
		codeAssistantConfig$: codeAssistantConfig,
		aISolutionsMaterialType$: materialType,
	} = useContainerData(SlAISolutionsContext, SlDataService, [
		'codeAssistantConfig$',
		'aISolutionsMaterialType$',
	]);
	const solutions = codeAssistantConfig?.find(
		(config) => config.materialType === materialType,
	);

	const openHandler = () => {
		actions$.next({
			type: ActionType.OnAISolutionsOpen,
			requestType: RequestType.Parallel,
			data: {
				solutionContext: context,
			},
		});
		setIsOpened(true);
		setIsWrapperOpened(true);
	};

	const closeHandler = (
		source: CloseAISolutionsClickSource,
		actionId?: SolutionActionType,
	) => {
		actions$.next({
			type: ActionType.OnAISolutionsClose,
			requestType: RequestType.Parallel,
			data: {
				actionId,
				closeAISolutionsClickSource: source,
			},
		});

		setIsOpened(false);
		setTimeout(() => {
			setIsWrapperOpened(false);
		}, 500);
	};

	useEffect(() => {
		actions$.next({
			type: ActionType.OnAISolutionsDataUpdate,
			requestType: RequestType.Parallel,
			data: solutionsData,
		});
	}, [solutionsData]);

	useEffect(() => {
		actions$.next({
			type: ActionType.OnAISolutionsMount,
			requestType: RequestType.Parallel,
		});
	}, []);

	if (solutionType === SolutionType.AnswerExplanation) {
		return <AIAnswerExplanation />;
	}

	const button = useMemo(() => {
		if (isTIYWidget) {
			return (
				<div
					onClick={openHandler}
					className={classNames('ai-solutions__open-btn-tiy', {
						light: isLightMode,
					})}
				>
					<KodieIcon />
					<span>
						{i18n.t('quiz.answer.navigation_buttin_title')}
					</span>
				</div>
			);
		}
		return OpenButton ? (
			<OpenButton onClick={openHandler} />
		) : (
			<Button
				onClick={openHandler}
				className={`ai-solutions__open-button${className ? ` ${className}` : ''
				}`}
			>
				<SolotutorIcon />
				{i18n.t('web-kodie.use-ai-button')}
			</Button>
		);
	}, [isLightMode]);

	return (
		<>
			{button}
			{isWrapperOpened && (
				<div className="ai-solutions">
					<SlExternalWindowPanel
						onClose={() => {
							closeHandler(CloseAISolutionsClickSource.Outside);
						}}
						isPanelOpen={isOpened}
						shouldAnimate
					>
						<div className="ai-solutions__main-section">
							<div
								className="ai-solutions__main-section__hide-item"
								onClick={() => {
									closeHandler(
										CloseAISolutionsClickSource.Hide,
									);
								}}
							>
								{i18n.t('web-kodie.hide-vertical-panel')}
								{' '}
								<IconChevronRightSmall />
							</div>
							<div className="ai-solutions__main-section__title">
								{i18n.t('web-kodie.solutions-title')}
							</div>
							<div className="ai-solutions__main-section__solutions">
								{solutions?.actions?.map((item) => (item.isVisible ? (
									<AISolution
										solutionData={item}
										key={item.action}
										backToCodeClickHandler={() => {
											closeHandler(
												CloseAISolutionsClickSource.BackToCode,
												item.action,
											);
										}}
									/>
								) : null))}
							</div>
						</div>
					</SlExternalWindowPanel>
				</div>
			)}
		</>
	);
}
